import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/Button/Button.tsx";
import IndicadorComLoading from "../../../components/IndicadorComLoading/IndicadorComLoading.tsx";
import { ColunaConfig, TabelaVirtualizadaGenerica } from "../../../components/TabelaVirtualizadaGenerica/TabelaVirtualizadaGenerica.tsx";
import { colunasEmpresaInicial } from "../../../contexts/ContextConstants.ts";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import { useTabela } from "../../../contexts/Tabela.js";
import { useToast } from "../../../contexts/ToastContext.tsx";
import MotionContainer from "../../../core/components/MotionContainer.tsx";
import MotionSemAutorizacao from "../../../core/components/MotionSemAutorizacao.tsx";
import Separator from "../../../core/components/Separator.tsx";
import BarraSearch from "../../../core/data-entry/BarraSearch.tsx";
import FiltroData from "../../../core/data-entry/FiltroData.tsx";
import SelectInputField from "../../../core/data-entry/SelectInputField.tsx";
import Layout from "../../../core/layout/Layout.tsx";
import { primeiroDiaDosMesFormatado, ultimoDiaDosMesFormatado } from "../../../core/utils/UtilityDates.ts";
import { useRowClick } from "../../../hooks/useRowClick.ts";
import useService from "../../../hooks/useService.ts";
import { BuscarDivergenciasSATProps, deletarDivergenciasSAT, DivergenciaSAT, getDivergenciasSAT, TemDiferencaEnum, verificarDivergenciasSAT } from "../../../services/ModuloGerenciador/DivergenciasSAT.ts";
import "./DivergenciasSAT.css";
import ModalVerificarCte from "./ModalVerificarCte/ModalVerificarCte.tsx";
import ModalConfirmacao2 from "components/ModalConfirmacao2/ModalConfirmacao2.tsx";

interface DivergenciasSATHeaderProps {
    params: Partial<BuscarDivergenciasSATProps>;
    setParams: React.Dispatch<React.SetStateAction<Partial<BuscarDivergenciasSATProps>>>;
    buscarViagens: () => void;
    onClear: () => void;
    ctesVerificados?: number;
    loading?: boolean;
    handleOpenVerificarCte: () => void;
    handleDeletarCte: () => void;
}

function DivergenciasSATHeader({ params, setParams, buscarViagens, onClear, ctesVerificados, loading, handleOpenVerificarCte, handleDeletarCte }: DivergenciasSATHeaderProps) {
    return (
        <div className="divergencias-sat-header-container">
            <IndicadorComLoading label="Número de CTEs verificados: " loading={loading} value={ctesVerificados} />
            <div className="divergencias-sat-filtros-container">
                <div className="divergencias-sat-data-container">
                    <FiltroData
                        title="Início"
                        value={params.previsaoEmbarqueInicio ?? ""}
                        onChange={(value) =>
                            setParams((prev: Partial<BuscarDivergenciasSATProps>) => ({
                                ...prev,
                                previsaoEmbarqueInicio: value,
                            }))
                        }
                    />
                    <div className="filtro-data-divider" />
                    <FiltroData
                        title="Fim"
                        titleFrente={false}
                        value={params.previsaoEmbarqueFinal ?? ""}
                        onChange={(value) =>
                            setParams((prev: Partial<BuscarDivergenciasSATProps>) => ({
                                ...prev,
                                previsaoEmbarqueFinal: value,
                            }))
                        }
                    />
                </div>
                <Separator size="md" direction="horizontal" />
                <div style={{
                    marginBottom: 20,
                }}>
                    <SelectInputField
                        label="Tem Diferença"
                        value={params.temDiferenca ?? TemDiferencaEnum.TODOS}
                        options={[
                            { title: "Todos", obj: TemDiferencaEnum.TODOS },
                            { title: "Sim", obj: TemDiferencaEnum.SIM },
                            { title: "Não", obj: TemDiferencaEnum.NAO },
                        ]}
                        onChange={(e) => {
                            setParams((prev: Partial<BuscarDivergenciasSATProps>) => ({
                                ...prev,
                                temDiferenca: e.obj,
                            }))
                        }
                        }
                    />
                </div>
                <Separator size="md" direction="horizontal" />
                <BarraSearch
                    buscarSearch={buscarViagens}
                    clearSearch={onClear}
                    placeholder="Digite o CTE"
                    disabled={loading}
                    value={!!params.cte ? params.cte?.toString() || "" : ""}
                    onChange={(e) => {
                        setParams((prev: Partial<BuscarDivergenciasSATProps>) => ({
                            ...prev,
                            cte: e ? Number(e) : undefined,
                        }));
                    }}
                />
                <Separator size="sm" direction="horizontal" />
                <div style={{ display: 'flex', gap: 10 }}>
                    <Button
                        icon="Check"
                        label="Verificar CTE"
                        disabled={loading}
                        onClick={handleOpenVerificarCte}
                        fullWidth
                    />
                    <Button
                        icon="Trash"
                        label="Deletar CTE"
                        disabled={loading}
                        backgroundColor="#B80909"
                        hoverBackgroundColor="#B80909"
                        onClick={handleDeletarCte}
                    />
                </div>
            </div>
        </div>
    );
};

function DivergenciasSAT() {
    const { profile, setContextoEmbarque, hasRegra, dadosFilial } = useProfile();
    const { showToast } = useToast();
    const { setTabelaRedux, tabelaRedux } = useTabela();
    const hasBuscadoDivergencias = useRef(false);
    const navigate = useNavigate();

    const [divergencias, setDivergencias] = useState<DivergenciaSAT[]>([]);
    const [semAutorizacao, setSemAutorizacao] = useState(false);
    const [buscarDivergenciasSATParams, setBuscarDivergenciasSATParams] = useState<Partial<BuscarDivergenciasSATProps>>({
        cte: 0,
        temDiferenca: TemDiferencaEnum.SIM,
        previsaoEmbarqueInicio: primeiroDiaDosMesFormatado,
        previsaoEmbarqueFinal: ultimoDiaDosMesFormatado,
    });
    const [openVerificarCteModal, setOpenVerificarCteModal] = useState(false);
    const [open, setOpen] = useState(false);
    const handleClose = () => {
        setOpen(false);
    };

    const handleOpenVerificarCte = () => {
        if (!tabelaRedux?.row?.cte) {
            showToast("Selecione um CTE para deletar.", "error");
            return;
        }

        setOpen(true);
    }

    const [isLoadingDivergencias, buscarDivergencias] = useService(
        getDivergenciasSAT, {
        autoStart: false,
        onData: (data) => {
            setDivergencias(data);
        },
        onError: (error) => {
            if (error?.response?.status === 404) {
                showToast('Nenhuma divergência SAT encontrada', 'info');
                return
            }
            showToast('Erro ao buscar divergências SAT', 'error');
        }
    }
    );

    const [isLoadingVerificarCTE, buscarCTE] = useService(
        verificarDivergenciasSAT,
        {
            autoStart: false,
            onData: (data) => {
                showToast('Verificação de divergências SAT concluída com sucesso.', 'success');
                setDivergencias([data]);
                setOpenVerificarCteModal(false);
            },
            onError: (error) => {
                showToast((error?.response?.data || error.message), 'error');
            }
        }
    );

    const contextoViagem = useMemo(() => {
        return dadosFilial?.find(filial => filial.nomeFilial === tabelaRedux.row?.unidadeEmbarque)?.contexto;
    }, [dadosFilial, tabelaRedux.row?.unidadeEmbarque]);

    const handleBuscarCTE = useCallback((cte: number) => {
        if (!profile?.token || !cte) return;
        buscarCTE({
            token: profile.token,
            cte,
        });
    }, [profile?.token, buscarCTE]);

    const deletarCte = useCallback(async () => {
        if (!profile?.token) return;

        if (!tabelaRedux?.row?.cte) {
            showToast("Selecione um CTE para deletar.", "error");
            return;
        }

        await deletarDivergenciasSAT({ token: profile?.token, cte: tabelaRedux.row.cte })
            .then(() => {
                handleBuscarViagens();
                setTabelaRedux({});
                showToast("CTE deletado com sucesso.", "success");
            })
            .catch((error) => {
                if (typeof error.response.data === "string") {
                    showToast(error.response.data, "error");
                } else {
                    showToast(
                        "Ocorreu um erro inesperado, volte e tente novamente, caso o problema persista entre em contato com o suporte.",
                        "error"
                    );
                }
            });
    }, [profile?.token, setTabelaRedux, tabelaRedux.colunaSelecionada]);

    const handleSingleClick = useCallback(
        (row: DivergenciaSAT) => {
            if (!row) return;
            setTabelaRedux({
                colunaSelecionada: row.idViagem,
                row,
            });
        },
        [setTabelaRedux]
    );

    const handleDoubleClick = useCallback(() => {
        if (!!tabelaRedux.colunaSelecionada) {
            if (!contextoViagem) {
                showToast("Contexto da viagem não encontrado.", "error");
                return;
            }
            setContextoEmbarque(contextoViagem);
            navigate(
                `/viagens/${contextoViagem}/cadastro/${tabelaRedux.colunaSelecionada}`
            );
        } else {
            showToast("Selecione uma viagem para editar.", "error");
        }
    }, [tabelaRedux, navigate, showToast, contextoViagem, setContextoEmbarque]);

    const handleRowClick = useRowClick({
        idKey: "idViagem",
        onSelect: handleSingleClick,
        onDouble: handleDoubleClick,
    });

    const getRowClassName = useCallback(
        (row: DivergenciaSAT) => {
            if (!tabelaRedux?.colunaSelecionada) return "";
            return row.idViagem === tabelaRedux.colunaSelecionada
                ? "selecionado"
                : "";
        },
        [tabelaRedux?.colunaSelecionada]
    );

    const handleCellClassName = useCallback(
        (coluna: ColunaConfig<DivergenciaSAT>, row: DivergenciaSAT, _rowIndex: number) => {
            const field = coluna?.field ?? coluna?.campo ?? coluna?.nome;
            if (!field) return undefined;

            const classes: string[] = [];

            const camposPiscar = [
                "freteMotoristaGerenciador",
                "freteMotoristaSat",
                "freteFiscalGerenciador",
                "freteFiscalSat",
                "pedagioGerenciador",
                "pedagioSat",
                "icmsGerenciador",
                "icmsSat",
            ];

            if (
                (row.difFreteMotorista && ["freteMotoristaGerenciador", "freteMotoristaSat"].includes(field)) ||
                (row.difFreteFiscal && ["freteFiscalGerenciador", "freteFiscalSat"].includes(field)) ||
                (row.difPedagio && ["pedagioGerenciador", "pedagioSat"].includes(field)) ||
                (row.difIcms && ["icmsGerenciador", "icmsSat"].includes(field))
            ) {
                classes.push("cell-vermelho");
            }

            if (camposPiscar.includes(field)) {
                classes.push("td-right");
            }

            return classes.length ? classes.join(" ") : undefined;
        },
        []
    );

    const handleClear = useCallback(() => {
        setBuscarDivergenciasSATParams({
            cte: 0,
            previsaoEmbarqueInicio: primeiroDiaDosMesFormatado,
            previsaoEmbarqueFinal: ultimoDiaDosMesFormatado,
            temDiferenca: TemDiferencaEnum.SIM,
        });
        buscarDivergencias({
            token: profile?.token!,
            cte: 0,
            temDiferenca: TemDiferencaEnum.SIM,
            previsaoEmbarqueInicio: primeiroDiaDosMesFormatado,
            previsaoEmbarqueFinal: ultimoDiaDosMesFormatado,
        });
    }, [profile?.token, buscarDivergencias]);

    const handleBuscarViagens = useCallback(() => {
        if (!profile?.token) return;
        buscarDivergencias({
            token: profile.token,
            cte: buscarDivergenciasSATParams.cte ?? 0,
            temDiferenca: buscarDivergenciasSATParams.temDiferenca,
            previsaoEmbarqueInicio: buscarDivergenciasSATParams.previsaoEmbarqueInicio!,
            previsaoEmbarqueFinal: buscarDivergenciasSATParams.previsaoEmbarqueFinal!,
        });
    }, [profile?.token, buscarDivergenciasSATParams, buscarDivergencias]);

    const biggerDevice = useMemo(() => {
        return window.innerWidth >= 1950;
    }, []);

    useEffect(() => {
        if (profile?.token && !hasBuscadoDivergencias.current) {
            setSemAutorizacao(!hasRegra("Divergencias SAT"));
            handleBuscarViagens();
            hasBuscadoDivergencias.current = true;
        }
    }, [profile?.token, hasRegra, buscarDivergencias, handleBuscarViagens]);

    const isLoading = useMemo(() => {
        return isLoadingDivergencias || !profile?.token || isLoadingVerificarCTE || (!divergencias.length && !hasBuscadoDivergencias.current);
    }, [isLoadingDivergencias, profile?.token, isLoadingVerificarCTE, divergencias.length]);

    if (semAutorizacao) {
        return <MotionSemAutorizacao />;
    }

    return (
        <Layout title="Divergências SAT">
            <MotionContainer animationKey="DivergenciasSAT">
                <div className="content-container">
                    <DivergenciasSATHeader loading={isLoading} handleDeletarCte={() => handleOpenVerificarCte()} handleOpenVerificarCte={() => setOpenVerificarCteModal(true)} ctesVerificados={divergencias.length} buscarViagens={handleBuscarViagens} params={buscarDivergenciasSATParams} setParams={setBuscarDivergenciasSATParams} onClear={handleClear} />
                    <TabelaVirtualizadaGenerica
                        dados={divergencias}
                        colunas={colunasEmpresaInicial.DivergenciasSAT}
                        rowHeight={20}
                        isLoading={isLoading}
                        getRowClassName={getRowClassName}
                        onRowClick={handleRowClick}
                        getCellClassName={handleCellClassName}
                        centralized={biggerDevice}
                        hasFooter={false}
                    />
                </div>
            </MotionContainer>
            <ModalVerificarCte
                isOpen={openVerificarCteModal}
                onClose={() => setOpenVerificarCteModal(false)}
                buscarCTE={handleBuscarCTE}
                isLoading={isLoadingVerificarCTE || isLoadingDivergencias}
            />
            <ModalConfirmacao2
                titulo="CTE"
                texto="Tem certeza que deseja deletar este CTE?"
                textoBotao="Confirmar"
                open={open}
                onClick={deletarCte}
                onClose={handleClose}
            />
        </Layout>
    )
};

export default memo(DivergenciasSAT);